import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AudioAIDialogLine, DropdownContent, SoundLibraryAI, AudioAI } from "../../types/types";
import { ConvertAudioOptions, voiceFromAudioAI } from "../common/AudioEditor/utils";
import { getProjects, getProject } from "../../api";
import Dropdown from "../common/Dropdown";
import TextInput from "../ui/TextInput";
import Icon from "../common/Icon";
import { useGlobalState } from "../../context/useGlobalState";
import closeCrossIcon from "../../assets/images/close-cross.svg";
import SoundLibraryModalList from "../lists/SoundLibraryModalList";
import ProjectAudioModalList from "../lists/ProjectAudioModalList";
import studio from "./StudioModal.module.css";
import { ModalPortal } from "./ModalPortal";

export interface SelectAudioModalProps {
    isOpen: boolean;
    onClose: () => void;
    /**
     * `sourceKind` tells the editor what the object really is: project audios are
     * flattened into the SoundLibraryAI shape and would otherwise pass for library
     * sounds instead of voices.
     */
    onSelectAudio: (audio: SoundLibraryAI | null, options?: ConvertAudioOptions) => void;
    placeholder?: string;
}

const SelectAudioModal: React.FC<SelectAudioModalProps> = ({ isOpen, onClose, onSelectAudio, placeholder }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [selectedProject, setSelectedProject] = useState<string>("");
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);

    const [projects, setProjects] = useState<DropdownContent[]>([]);
    const [projectData, setProjectData] = useState<any>(null);

    const [loading, setLoading] = useState<boolean>(true);

    const [error, setError] = useState<string | null>(null);
    const token = localStorage.getItem("token") || "";

    const [audioSource, setAudioSource] = useState<"project" | "library" | null>(null);
    const [search, setSearch] = useState("");

    const showSearch =
        audioSource === "library" || (audioSource === "project" && !!selectedProject && !!selectedContentLanguage);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // react-select menus are portaled to document.body
            if (target.closest(".rw-select__menu") || target.closest(".rw-select__menu-portal")) {
                return;
            }
            if (modalContentRef.current && !modalContentRef.current.contains(target)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    const languagesAIOptions: DropdownContent[] =
        data?.languagesAI?.map((language) => ({
            value: language.code,
            label: language.label,
        })) || [];

    const filteredAudioOptions: SoundLibraryAI[] =
        projectData?.audioAI
            ?.filter((audio: AudioAI) => audio.language === selectedContentLanguage)
            ?.filter((audio: AudioAI) => audio.audioUrl)
            ?.sort((a: AudioAI, b: AudioAI) => a.position - b.position)
            .map((audio: AudioAI) => ({
                id: audio.id,
                url: audio.audioUrl,
                name: audio.name,
                soundDescription: audio.text || "",
            })) || [];

    const handleProjectAudioSelection = (audio: SoundLibraryAI) => {
        // The list shows a flattened shape; the capsule itself still holds who recorded
        // it and its transcript, both of which the editor needs to keep.
        const capsule = projectData?.audioAI?.find((item: AudioAI) => item.id === audio.id);
        onSelectAudio(audio, {
            sourceKind: "audioAI",
            text: capsule?.text ?? null,
            // Same rule as everywhere else: nothing to say means no voice block at all.
            voice: capsule ? voiceFromAudioAI(capsule) : undefined,
            // A capsule that is itself a dialogue keeps its turns, so a montage does not
            // collapse them into one voice when translated.
            dialogLines: (capsule?.dialogLines || []).map(
                (line: AudioAIDialogLine & { voiceAI?: { name?: string } }) => ({
                    order: line.order,
                    text: line.text,
                    voiceAIId: line.voiceAIId ?? null,
                    voiceCloneId: line.voiceCloneId ?? null,
                    voicePromptId: line.voicePromptId ?? null,
                    voiceName: line.voiceAI?.name ?? null,
                }),
            ),
        });
        onClose();
    };

    const handleLibraryAudioSelection = (audio: SoundLibraryAI) => {
        onSelectAudio(audio, { sourceKind: "soundLibrary" });
        onClose();
    };

    const fetchProject = useCallback(
        async (id: string) => {
            if (!token || !id) return;
            try {
                const response = await getProject({ token, projectId: id });
                if (response?.id) setProjectData(response);
                else console.error("Error fetching project");
            } catch {
                console.error("Error fetching project");
            }
        },
        [token],
    );

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                if (!token) {
                    setError(t("noToken"));
                    return;
                }
                const response = await getProjects({ token });
                const projectsList = response.map((project: { id: string; name: string }) => ({
                    label: project.name,
                    value: project.id,
                }));
                setProjects(projectsList);
            } catch {
                setError(t("errorFetchingAudio"));
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [token, t]);

    useEffect(() => {
        if (selectedProject && audioSource === "project") {
            fetchProject(selectedProject);
        }
    }, [selectedProject, fetchProject, audioSource]);

    if (!isOpen) return null;

    const modalTitle = placeholder || t("selectAudioEdit");

    return (
        <ModalPortal>
            <div className={studio.overlay}>
                <div className={studio.editorContent} ref={modalContentRef}>
                    <div className={studio.importTop}>
                        <h2 className={studio.importTitle}>{modalTitle}</h2>
                        <button type="button" className={studio.importClose} onClick={onClose} aria-label={t("close")}>
                            <img src={closeCrossIcon} alt="" />
                        </button>
                    </div>
                    <div className={studio.importToolbar}>
                        <Dropdown
                            label={t("source") || t("sourceLanguage")}
                            options={[
                                { value: "project", label: t("projects") },
                                { value: "library", label: t("soundLibrary") },
                            ]}
                            value={audioSource}
                            onChange={(value) => {
                                setAudioSource(value as "project" | "library");
                                setSelectedProject("");
                                setSelectedContentLanguage(null);
                                setSearch("");
                            }}
                            placeholder={t("selectType")}
                            loading={false}
                            error={false}
                            fullWidth
                            isWithoutMargin
                        />
                        {audioSource === "project" && (
                            <Dropdown
                                label={t("project")}
                                options={projects}
                                value={selectedProject}
                                onChange={(value) => {
                                    setSelectedProject(value);
                                    setSelectedContentLanguage(null);
                                    setSearch("");
                                }}
                                placeholder={t("selectProject")}
                                loading={loading}
                                error={!!error}
                                fullWidth
                                isWithoutMargin
                            />
                        )}

                        {selectedProject && audioSource === "project" && (
                            <Dropdown
                                label={t("language")}
                                options={languagesAIOptions}
                                value={selectedContentLanguage}
                                onChange={(value) => {
                                    setSelectedContentLanguage(value);
                                    setSearch("");
                                }}
                                placeholder={t("selectALanguage")}
                                loading={false}
                                error={false}
                                fullWidth
                                isWithoutMargin
                            />
                        )}

                        {showSearch && (
                            <label className={studio.importFldSearch}>
                                <span>{t("search")}</span>
                                <div className={studio.importSearch}>
                                    <Icon name="ic-search" className={studio.importSearchIcon} size={17} />
                                    <TextInput
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={t("searchByNameOrDescription") || "Rechercher un son…"}
                                    />
                                </div>
                            </label>
                        )}
                    </div>

                    <div className={studio.importList}>
                        {audioSource === "library" && (
                            <SoundLibraryModalList onClick={handleLibraryAudioSelection} search={search} />
                        )}

                        {audioSource === "project" && selectedProject && selectedContentLanguage && (
                            <ProjectAudioModalList
                                audioOptions={filteredAudioOptions}
                                onClick={handleProjectAudioSelection}
                                search={search}
                            />
                        )}

                        {audioSource === "project" && (!selectedProject || !selectedContentLanguage) && (
                            <div className={studio.noItems}>{t("pleaseSelectProjectAndLanguage")}</div>
                        )}

                        {audioSource !== "library" && audioSource !== "project" && (
                            <div className={studio.noItems}>{t("pleaseSelectATypeOfSounds")}</div>
                        )}
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default SelectAudioModal;
